import { Snackbar } from '@mui/material';

import CheckoutFooter from '@checkout/components/CheckoutFooter';
import CheckoutSummary from '@checkout/components/CheckoutSummary';
import Coupon from '@checkout/components/Coupon';
import PaymentsUnavailableBanner from '@checkout/components/PaymentsUnavailableBanner';
import PlanCard from '@checkout/components/PlanCard';
import StripePayment from '@checkout/components/StripePayment';
import Text from '@components/Text';
import { t } from '@i18n';

import type { useCheckoutPage } from '../hooks/useCheckoutPage';
import type { useCheckoutPayments } from '../hooks/useCheckoutPayments';
import { AsaasPaymentScreen } from './AsaasPaymentScreen';
import { CheckoutContactScreen } from './CheckoutContactScreen';
import './CheckoutPageView.scss';

type CheckoutPageState = ReturnType<typeof useCheckoutPage>;
type CheckoutPaymentActions = ReturnType<typeof useCheckoutPayments>;

interface CheckoutPageViewProps {
  checkout: CheckoutPageState;
  payment: CheckoutPaymentActions;
}

export function CheckoutPageView({ checkout, payment }: CheckoutPageViewProps) {
  return (
    <>
      <div className={`checkout-shell${checkout.paymentsBlocked ? ' checkout-shell--payments-blocked' : ''}`}>
        <CheckoutSummary
          plan={checkout.planForDisplay}
          valuesRef={checkout.valuesRef}
          provider={checkout.provider}
          onPlanSelect={checkout.onPlanSelect}
        />

        <main className='checkout-form-panel'>
          <div className='checkout-page'>
            <CheckoutContactScreen
              valuesRef={checkout.valuesRef}
              selectedCountry={checkout.selectedCountry}
              locale={checkout.locale}
              usdAvailable={checkout.usdAvailable}
              onCountryChange={checkout.onCountryChange}
            />

            {checkout.isStripe ? <>
              <div className='input-group mt-3'>
                <Coupon valuesRef={checkout.valuesRef} onCouponApplied={checkout.refresh} />
              </div>

              <Text.h5 className='mt-3'>{t('checkout.cardData')}</Text.h5>
              <StripePayment
                key={`stripe-${checkout.plan.id}-${checkout.stripeCatalog?.stripeAccount || 'platform'}`}
                publishableKey={checkout.stripePublishableKey}
                stripeAccount={checkout.stripeCatalog?.stripeAccount || null}
                locale={checkout.locale.code}
                preValidate={payment.preValidateStripe}
                createIntent={payment.createStripeIntent}
                getBillingDetails={payment.getStripeBillingDetails}
                onConfirmed={payment.handleStripeConfirmed}
                onError={payment.handleStripeError}
                submitDisabled={checkout.paymentsBlocked}
                beforeSubmit={(
                  <PlanCard
                    plan={checkout.planForDisplay}
                    valuesRef={checkout.valuesRef}
                    className='checkout-plan-card--inline'
                  />
                )}
              />
            </> : <AsaasPaymentScreen
              valuesRef={checkout.valuesRef}
              plan={checkout.plan}
              planForDisplay={checkout.planForDisplay}
              provider={checkout.provider}
              methodsEnabled={checkout.methodsEnabled}
              refreshKey={checkout.refreshKey}
              refresh={checkout.refresh}
              pixInfo={checkout.pixInfo}
              isLoading={payment.isAsaasLoading}
              onPay={payment.handleAsaasPayment}
              submitDisabled={checkout.paymentsBlocked}
            />}

            <CheckoutFooter />
          </div>
        </main>
      </div>

      {checkout.paymentsBlocked && <PaymentsUnavailableBanner />}

      <Snackbar
        open={!!checkout.snackbar}
        onClose={() => checkout.setSnackbar(null)}
        message={checkout.snackbar ? <Text.span>{checkout.snackbar}</Text.span> : null}
      />
    </>
  );
}
